/**
 * Quiz modals (<dialog class="em-quiz-dialog">).
 *
 * Opened by [data-em-quiz-open="{dialog id}"] buttons, or by links to a
 * dialog's trigger hash (data-em-quiz-hash), including on page load.
 * The trigger hash deliberately differs from the dialog's ID so the child
 * theme's dialog.js, which opens any #id-linked <dialog> with show(), leaves
 * these alone.
 *
 * The dialog opens non-modal (show(), not showModal()), styled as a fixed
 * overlay that is its own backdrop. showModal() puts the dialog in the
 * browser's top layer and makes the rest of the page inert, so nothing added
 * outside it can show above it: not HubSpot's reCAPTCHA challenge, which
 * blocked the lead form's submit, and not the BugHerd sidebar. The rest of the
 * page is made inert here instead, leaving those two out.
 *
 * @package ppd
 * @since   1.0.27
 */

function dialogForHash(hash) {
	if (!hash) {
		return null;
	}
	return document.querySelector(`dialog.em-quiz-dialog[data-em-quiz-hash="${window.CSS.escape(hash)}"]`);
}

/**
 * Dialog a link points at, if the link targets this page.
 *
 * @param {HTMLAnchorElement} link
 * @return {HTMLDialogElement|null}
 */
function dialogForLink(link) {
	const href = link.getAttribute('href') || '';
	if (!href.includes('#')) {
		return null;
	}
	const url = new URL(href, window.location.href);
	const samePage = href.startsWith('#')
		|| (url.origin === window.location.origin && url.pathname === window.location.pathname);
	return samePage ? dialogForHash(url.hash.slice(1)) : null;
}

// Page elements that must stay usable over an open quiz: the reCAPTCHA
// challenge popup (HubSpot lead form; Google appends it to <body>, unlike the
// badge, which stays inside the form) and the BugHerd sidebar.
const KEEP_USABLE = 'iframe[src*="recaptcha"][src*="/bframe"], [id*="bugherd" i], [class*="bugherd" i]';

/**
 * Make everything outside the dialog inert, as showModal() would.
 *
 * @param {HTMLDialogElement} dialog
 */
function inertPage(dialog) {
	dialog.emQuizInerted = [];
	for (let node = dialog; node.parentElement && node !== document.body; node = node.parentElement) {
		Array.from(node.parentElement.children).forEach((sibling) => {
			if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)
				|| sibling.matches(KEEP_USABLE) || sibling.querySelector(KEEP_USABLE)) {
				return;
			}
			sibling.inert = true;
			dialog.emQuizInerted.push(sibling);
		});
	}
}

function restorePage(dialog) {
	(dialog.emQuizInerted || []).forEach((element) => {
		element.inert = false;
	});
	dialog.emQuizInerted = [];
}

function onKeydown(event) {
	const dialog = event.currentTarget;
	if (event.key === 'Escape' && dialog.open) {
		dialog.close();
	}
}

function onClose(event) {
	const dialog = event.currentTarget;
	restorePage(dialog);
	if (!document.querySelector('dialog.em-quiz-dialog[open]')) {
		document.documentElement.classList.remove('em-quiz-dialog-open');
	}
	const opener = dialog.emQuizOpener;
	if (opener && opener.focus && document.contains(opener)) {
		opener.focus();
	}
}

function openDialog(dialog, opener) {
	if (!dialog || dialog.open) {
		return;
	}
	if (!dialog.emQuizBound) {
		dialog.emQuizBound = true;
		dialog.addEventListener('close', onClose);
		dialog.addEventListener('keydown', onKeydown);
	}
	dialog.emQuizOpener = opener || document.activeElement;
	dialog.show();
	inertPage(dialog);
	document.documentElement.classList.add('em-quiz-dialog-open');
	const quiz = dialog.querySelector('[data-em-quiz]');
	if (quiz) {
		quiz.dispatchEvent(new CustomEvent('em-quiz:open'));
	}
}

export function initModals() {
	document.addEventListener('click', (event) => {
		const target = event.target instanceof Element ? event.target : null;
		if (!target) {
			return;
		}

		const opener = target.closest('[data-em-quiz-open]');
		if (opener) {
			event.preventDefault();
			openDialog(document.getElementById(opener.dataset.emQuizOpen), opener);
			return;
		}

		const closer = target.closest('[data-em-quiz-close]');
		if (closer) {
			const dialog = closer.closest('dialog');
			if (dialog && dialog.open) {
				dialog.close();
			}
			return;
		}

		// Backdrop click: the event target is the dialog itself.
		if (target.matches('dialog.em-quiz-dialog[open]')) {
			target.close();
			return;
		}

		const link = target.closest('a[href]');
		const dialog = link ? dialogForLink(link) : null;
		if (dialog) {
			event.preventDefault();
			openDialog(dialog, link);
		}
	});

	const openFromHash = () => openDialog(dialogForHash(window.location.hash.slice(1)), null);
	window.addEventListener('hashchange', openFromHash);
	openFromHash();
}
